// The nine business groups behind "AI across the homebuilding lifecycle" on
// the dashboard: six lifecycle stages plus three functions that support them
// all. Initiatives and library tools are sorted into groups automatically
// from their free-text team / department, using `match` below.

export type BusinessGroupId =
  | "investments"
  | "land"
  | "build"
  | "sales"
  | "marketing"
  | "care"
  | "finance"
  | "tech"
  | "corporate";

export type BusinessGroup = {
  id: BusinessGroupId;
  title: string;
  /** For cards and buttons. */
  short: string;
  /** Lifecycle stage, or a function that supports every stage. */
  kind: "stage" | "support";
  /** The departments and functions that roll up here. */
  includes: string;
  /** Starter ideas, shown while a group has no AI work yet. */
  ideas: string[];
};

export const BUSINESS_GROUPS: BusinessGroup[] = [
  {
    id: "investments",
    title: "Investments & Asset Management",
    short: "Investments",
    kind: "stage",
    includes: "Investments, Asset Management, Portfolio Management, strategic investments and joint ventures",
    ideas: ["Summarize investment memos and market reports", "Track portfolio performance against plan"],
  },
  {
    id: "land",
    title: "Land Acquisition & Development",
    short: "Land",
    kind: "stage",
    includes:
      "Land Acquisition, Development, Land Development, Community Development, Land Operations, Planning and Entitlements, Commercial Development",
    ideas: ["Compare land deals, pricing and supply by market", "Summarize due-diligence, zoning and title documents"],
  },
  {
    id: "build",
    title: "Homebuilding & Construction",
    short: "Homebuilding",
    kind: "stage",
    includes:
      "Construction, Housing Operations, Field Operations, Site Management, Purchasing, Estimating, Forward Planning, Design and Options",
    ideas: ["Turn drawings into quantity takeoffs and estimates", "Flag schedule risk from trade, permit and weather data"],
  },
  {
    id: "sales",
    title: "Sales",
    short: "Sales",
    kind: "stage",
    includes: "Sales, Online Sales, Community Sales, Sales Management, Escrow, customer-facing Interior Design and Design Centre",
    ideas: ["Draft follow-ups to buyer leads in minutes", "Answer buyer questions about plans, options and pricing"],
  },
  {
    id: "marketing",
    title: "Marketing & Customer Experience",
    short: "Marketing & CX",
    kind: "stage",
    includes:
      "Marketing, Digital Marketing, Corporate Marketing and Brand, Customer Experience, Product & Engineering for digital customer experiences",
    ideas: ["Write community and listing copy in our brand voice", "Turn survey comments into themes and actions"],
  },
  {
    id: "care",
    title: "Customer Care",
    short: "Customer Care",
    kind: "stage",
    includes: "Customer Care, Warranty and In-Home Service, Customer Care Technicians",
    ideas: ["Triage warranty requests to the right trade", "Spot recurring defects across communities"],
  },
  {
    id: "finance",
    title: "Finance & Accounting",
    short: "Finance",
    kind: "support",
    includes: "Finance, Accounting, FP&A, Accounts Payable, Treasury, Taxation, Financial Reporting, Controllers",
    ideas: ["Audit financial models for errors", "Match invoices to purchase orders"],
  },
  {
    id: "tech",
    title: "Technology & Data",
    short: "Technology & Data",
    kind: "support",
    includes:
      "Information Technology, Enterprise Applications, Infrastructure, IT Service Management, BI & Analytics, field and business applications",
    ideas: ["Answer IT how-to questions from our own docs", "Draft reports from BI data in plain English"],
  },
  {
    id: "corporate",
    title: "Corporate & Shared Services",
    short: "Corporate",
    kind: "support",
    includes: "People and HR, Legal, Risk Management, Administration and Office Services, Corporate Communications",
    ideas: ["Answer policy questions from our handbooks", "Review contracts for key terms and risks"],
  },
];

export const GROUP_BY_ID = new Map(BUSINESS_GROUPS.map((g) => [g.id, g]));

// Checked in this order and the first match wins, so a "Land & Housing
// Marketing" team is Marketing and "Housing Finance" is Finance.
const MATCH: [BusinessGroupId, RegExp][] = [
  ["care", /customer care|warranty|in-home|homeowner|technician/],
  ["finance", /financ|accounting|fp&a|payable|receivable|treasury|\btax|controller|payroll/],
  ["marketing", /marketing|brand|customer experience|\bcx\b|digital experience|product & engineering/],
  ["sales", /\bsales\b|escrow|interior design|design cent(er|re)|design studio/],
  ["tech", /\bit\b|information technology|technolog|software|enterprise app|infrastructure|service management|business intelligence|\bbi\b|analytics|\bdata\b|\bai\b/],
  ["corporate", /\bhr\b|human resources|people|legal|risk|insurance|safety|admin|office services|communications|\bceo\b|executive|corporate|shared services/],
  ["investments", /invest|asset management|portfolio|joint venture/],
  ["land", /\bland\b|entitle|community development|commercial development|business development|acquisi|\bplanning\b/],
  ["build", /construct|housing operations|field operations|\boperations\b|site manag|purchas|estimat|forward planning|design|options|product development|build/],
];

/** The group for a free-text team or department, or null if none fits. */
export function groupOf(team: string | undefined): BusinessGroupId | null {
  const t = (team ?? "").toLowerCase();
  return t ? MATCH.find(([, re]) => re.test(t))?.[0] ?? null : null;
}
